import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Users, Mail, Phone, Search, Calendar } from 'lucide-react';
import api from '../api/axios';

const ClientsPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchClients();
  }, [navigate]);

  const fetchClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast({ title: 'Acces refuse', description: 'Reserve aux administrateurs', variant: 'destructive' });
        navigate('/staff/dashboard');
      } else {
        toast({ title: 'Erreur', variant: 'destructive' });
      }
    } finally { setLoading(false); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Cayenne' });
    } catch { return '-'; }
  };

  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      c.nom?.toLowerCase().includes(s) ||
      c.prenom?.toLowerCase().includes(s) ||
      c.email?.toLowerCase().includes(s) ||
      c.telephone?.includes(s)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/staff/dashboard')} className="text-white hover:bg-white/10" data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2"><Users size={22} /> Liste des Clients</h1>
              <p className="text-xs text-gray-300">{clients.length} client{clients.length > 1 ? 's' : ''} enregistre{clients.length > 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="relative mb-5" data-testid="client-search">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom, email ou telephone..."
            className="pl-10 h-11"
            data-testid="client-search-input"
          />
        </div>

        {loading ? (
          <div className="text-center py-20 text-stone-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-stone-400">
            {search ? 'Aucun client trouve' : 'Aucun client enregistre'}
          </div>
        ) : (
          <div className="grid gap-3">
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-5 gap-4 px-5 py-2 text-xs font-semibold text-gray-500 uppercase">
              <span>Nom</span>
              <span>Prenom</span>
              <span>Email</span>
              <span>Telephone</span>
              <span>Derniere reservation</span>
            </div>
            {filtered.map(client => (
              <Card key={client.id} className="border border-stone-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`client-${client.id}`}>
                <CardContent className="p-4">
                  {/* Desktop */}
                  <div className="hidden md:grid grid-cols-5 gap-4 items-center text-sm">
                    <span className="font-semibold text-stone-800">{client.nom}</span>
                    <span className="text-stone-700">{client.prenom}</span>
                    <span className="flex items-center gap-1.5 text-stone-600 truncate">
                      <Mail size={13} className="shrink-0 text-gray-400" /> {client.email}
                    </span>
                    <span className="flex items-center gap-1.5 text-stone-600">
                      <Phone size={13} className="shrink-0 text-gray-400" /> {client.telephone}
                    </span>
                    <span className="flex items-center gap-1.5 text-stone-500 text-xs">
                      <Calendar size={13} className="shrink-0 text-gray-400" /> {formatDate(client.last_reservation_at)}
                    </span>
                  </div>
                  {/* Mobile */}
                  <div className="md:hidden space-y-1.5">
                    <p className="font-semibold text-stone-800">{client.prenom} {client.nom}</p>
                    <p className="flex items-center gap-1.5 text-xs text-stone-600"><Mail size={12} /> {client.email}</p>
                    <p className="flex items-center gap-1.5 text-xs text-stone-600"><Phone size={12} /> {client.telephone}</p>
                    <p className="flex items-center gap-1.5 text-xs text-stone-500"><Calendar size={12} /> {formatDate(client.last_reservation_at)}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsPage;
